<template>
  <div>
    <mf-table
      ref="shipItemsTable"
      :tableProps="tab.tableProps"
      :loadData="getLoadData()"
      :hideGutter="true"
      :key="refreshKey"
      :tab="tab"
      :isLocallyFiltered="true"
      @cellClicked="onTableCellClicked"
      :rowData="rowData"
      tableClass="shipItem"
      :refreshCard="refreshCard"
    >
      <template v-slot:itemDelete="{ rowData: item, rowField }">
        <field-card-edit-action
          :rowData="item"
          :rowField="{ ...rowField, isRemoveDisabled: (row) => disableDelete(row)}"
          :deliveries="rowData.partialShipments"
          @cell-clicked="onDeleteClicked"
        />
      </template>
      <template v-slot:itemDelivery="{ rowData: item, rowField }">
        <field-ship-items-icon
          :rowData="item"
          :rowField="rowField"
          :deliveryLength="rowData.partialShipments">

        </field-ship-items-icon>
      </template>
      <template v-slot:qtyReceived="{ rowData }">
        <div  class="has-text-right">
          {{ getQtyReceived(rowData) }}
        </div>
      </template>
      <template v-slot:qtyNotScheduled="{ rowData: item }">
        <div  class="has-text-right" v-if="($_.get(item, unscheduledField) ?? 0) === 0">
          {{ $_.get(item, unscheduledField) ?? 0 }}
        </div>
        <button
           v-else
           class="button item-count-not-scheduled"
          :disabled="($_.get(item, unscheduledField) ?? 0) === 0"
          @click="handleScheduleClick(item)">
          {{ $_.get(item, unscheduledField) ?? 0 }}
        </button>
      </template>
      <template #qtyShort="{ rowData: item }">
        <VDropdown :triggers="['hover']">
          <div  class="has-text-right" v-if="($_.get(item, 'qtyShort') ?? 0) === 0">
            {{ $_.get(item, 'qtyShort') ?? 0 }}
          </div>
          <button v-else
            class="button has-background-red item-count-not-scheduled"
            :disabled="($_.get(item, 'qtyShort') ?? 0) === 0"
            @click="handleQtyShort(item, 'receive')">
            {{ $_.get(item, 'qtyShort') ?? 0 }}
          </button>
          <template #popper v-if="item.ignoredShort">
            <div class="card"
              v-html="getPopupContent('Received', item.name, item.qtyShort, item.ignoredShort)">
            </div>
          </template>
        </VDropdown>
      </template>
      <template #qtyShortLoad="{ rowData: item }">
        <VDropdown :triggers="['hover']">
          <div  class="has-text-right" v-if="($_.get(item, 'qtyShortLoad') ?? 0) === 0">
            {{ $_.get(item, 'qtyShortLoad') ?? 0 }}
          </div>
          <button v-else
            class="button has-background-red item-count-not-scheduled"
            :disabled="($_.get(item, 'qtyShortLoad') ?? 0) === 0"
            @click="handleQtyShort(item, 'load')">
            {{ $_.get(item, 'qtyShortLoad') ?? 0 }}
          </button>
          <template #popper v-if="item.ignoredShortLoad">
            <div class="card"
              v-html="getPopupContent('Load', item.name, item.qtyShortLoad, item.ignoredShortLoad)">
            </div>
          </template>
        </VDropdown>
      </template>
    </mf-table>
    <o-loading 
        :full-page="true" 
        :active="isLoading" 
        :can-cancel="false">
    </o-loading>
    <o-modal :active="qtyShortOptionsModal !== ''" :canCancel="false" class="modal-xs" v-if='qtyShortOptionsModal'>
      <div class="modal-card">
        <header class="modal-card-head">
          <h4 class="modal-card-title">
            Item QTY Short {{shortTypeLabel}}
          </h4>
          <div class="is-divider-vertical"></div>
          <i class="icon-close is-clickable" @click="cancel()"></i>
        </header>
        <section class="modal-card-body is-size-3">
          <div class="is-italic has-text-weight-semibold mb-2 is-size-3">Item</div>
          {{ selectedShortItem.name }}
					<div class="is-divider my-4 is-dark"></div>
          <div v-for="(opt, index) in shortQtyOptions" :key="opt.key" class="has-text-black-bis">
            <div class="is-flex is-align-items-center">
              <input type="radio" :id="opt.key"
                :value="opt.key" v-model="selectedQtyShortOption"
                @click="selectedQtyShortOption = opt.key"
                :checked="selectedQtyShortOption === opt.key"
              />
              <label :for="opt.key" class="has-text-black-bis is-italic">{{opt.title}} <span v-if="opt.key === 'ignoreShort'">
                {{shortTypeLabel}}
              </span></label>
            </div>
            <h5 class="pt-4 line-height">{{opt.message}}</h5>
            <div class="is-divider my-4" v-if="!index"></div>
          </div>
        </section>
        <footer class="modal-card-foot is-justify-content-flex-end">
          <button class="button is-outlined" @click="closeQtyShortOptionModal">Cancel</button>
          <button class="button has-background-black-bis"
            @click="executeShortOption"
            :disabled="!selectedQtyShortOption">Continue</button>
          </footer>
      </div>
    </o-modal>

  </div>
</template>

<script>
import {
  defineComponent,
  defineAsyncComponent,
  reactive,
  toRefs,
  ref,
  computed,
} from 'vue';
import {
  every,
  get,
  set,
  find
} from 'lodash';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { v4 as uuid } from 'uuid';
import LocalSort from '@/components/utils/LocalSort';
import ShippingItemsIconField from '@/components/table-fields/ShippingItemsIconField.vue';
import CardEditActionField from '@/components/table-fields/CardEditActionField.vue';
import ScheduleItems from '../modals/ScheduleItems.vue';
import { ModalProgrammatic } from '@oruga-ui/oruga-next';
import { DialogProgrammatic } from '@/components/Dialog';
import { useToast } from 'vue-toastification';
import Shipping from '../../models/Shipping';

const MfTable = defineAsyncComponent(() => import('@/components/table-fields/MfTable.vue'));

export default defineComponent({
  name: 'ShippingItemsTab',
  emits: [],
  props: {
    tab: {
      type: Object,
      default: () => {},
    },
    rowData: {
      type: Object,
      default: () => {},
    },
    deliveryDetails: {
      type: [Array, Object],
      default: () => [],
    },
    refreshCard: Function,
  },
  components: {
    MfTable,
    ShippingItemsIconField,
    CardEditActionField,
    ScheduleItems,
  },


  setup(props, { emit }) {
    let { rowData } = props;
    const {
      tab
    } = props;
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const shipItemsTable = ref(null);
    const state = reactive({
      refreshKey: 0,
      localItems: [...(props.rowData.items)],
      isLoading: false,
      unscheduledField: props.rowData.shipType === 's-m' ? 'masterSummary.notScheduled': 'quantity',
      qtyShortOptionsModal: '',
      selectedShortItem: {},
      selectedQtyShortOption: ''
    })

    const zeroId = '000000000000000000000000';

    const shortTypeLabel = computed(() => state.qtyShortOptionsModal === 'receive' ? '(Received)': '(Load)')

    const shortQtyOptions = computed(() => {
      return [
        {
          title: 'Shedule Delivery',
          key: 'addDelivery',
          message: `Add Short QTY ${shortTypeLabel.value} to new or existing delivery.`,
        }, {
          title: 'Ignore Short QTY',
          key: 'ignoreShort',
          message: 'Ignoring “Short QTY” will remove the ability to reschedule this item and clear this shortage as a blocker. The short QTY will remain noted in the associated delivery.',
        }
      ]
    })
    
    const onTableCellClicked = async (params) => {
      const { type, data } = params;
      if (type === 'refresh-table') {
        await props.refreshCard();
      }

      else if (type === 'scheduleItems') {
        const filteredItems = (props.rowData.items || []).filter(
          i => (i.masterSummary?.notScheduled ?? 0) > 0
        );

        ModalProgrammatic.open({
          component: ScheduleItems,
          props: {
            card: data,
            masterShipment: {
              ...props.rowData,
              items: filteredItems,
            },
            isActive: true,
          },
          rootClass: 'modal-sm',
          trapFocus: true,
          canCancel: false,
          events: {
            close: async () => {
              await props.refreshCard();
            },
          },
        });
      } else if (type === 'qtyShort') {
        state.selectedShortItem = data;
      }
    }

    const loadAsyncData = async () => {
      state.tableData = LocalSort.filter(
        get(rowData, 'items'),
        {
          sortField: 'name',
          direction: 'asc',
        },
        true,
      );
      // set a unique id for each item
      if (tab.id === 'items') {
        for (const item of state.tableData) {
          if (item.source && item.source.from === 'inventory') {
            set(item, 'isInventoryItem', true);
          }
          if (['shipping-card-edit'].includes(store.state.activeScreen) && route.params.cardId === 'add') {
            set(item, 'quantity', item.available || 0);
            set(item, 'selectedQty', item.available || 0);
          }
          if (!item.uid) {
            set(item, 'uid', uuid());
          }
        }
      }
      state.tableData.forEach(i => {
        i.qtyUnscheduled = 0;
        i.qtyScheduled = 0;
        i.qtyInDelivery = 0;
        i.qtyReceived = 0;
        i.qtyShort = 0;
        i.ignoredShort = 0;
        i.qtyShortLoad = 0;
        i.ignoredShortLoad = 0;
      });
      for(const cSL of rowData.partialShipments){
        console.log('cSL', cSL.isLoadingComplete);
        for(const item of cSL.items){
          const matchedItem = find(state.tableData, i => {
            if (item._id === zeroId) {
              if (item.catId) {
                return i.catId === item.catId;
              } else {
                return i.uid === item.uid;
              }
            }
            return i._id.toString() === item._id.toString()
          });
          if(matchedItem){
              const isShortReDelivered = get(item, 'isDeliveredToPartial.isPicked', false)
              if (item.isUnderDeliverIgnored) {
                matchedItem.ignoredShort += item.underDeliver
              }
              if (item.isLoadIgnored) {
                matchedItem.ignoredShortLoad += item.qtyShortLoad;
              }
              console.log('cSL.isLoadingComplete', cSL.isLoadingComplete);
              if(cSL.isLoadingComplete && !item.isLoadIgnored){
                console.log('item.qtyShortLoad', item.qtyShortLoad);
                matchedItem.qtyShortLoad += (item.qtyShortLoad || 0);

              }
              if(cSL.isUnderDeliver && !isShortReDelivered && !item.isUnderDeliverIgnored){
                matchedItem.qtyShort += (item.underDeliver || 0);
              }
              if(cSL.status  === 'un-scheduled'){
                matchedItem.qtyUnscheduled += (item.quantity || 0);
              } else if(cSL.status  === 'not-started'){
                matchedItem.qtyScheduled += (item.quantity || 0);
              } else if (cSL.status  === 'in-transit'){
                matchedItem.qtyInDelivery += (item.quantity || 0);
              } else if (['completed', 'in-storage', 'fulfilled', 'zombie', 'released-to-inventory'].includes(cSL.status)){
                matchedItem.qtyReceived += ((item.quantity - item.underDeliver)|| 0);
              }
          }
        }
      }
      const isQtyAvailable = (item) => every(rowData.partialShipments, (partial) => !partial.items.some((i) => i.catId === item.catId));
      state.tableData = state.tableData.filter((item) => isQtyAvailable(item) ? item.quantity !== 0 || item.qtyShortLoad !== 0 : item.actualQty > 0);
      return { data: state.tableData, total: state.tableData.length };
    };

    const loadData = () => {
      let items = get(rowData, 'items', []);
      return items;
    };

    const handleScheduleClick = (item) => {
      if ((get(item, state.unscheduledField)?? 0) > 0) {
        onTableCellClicked({ type: 'scheduleItems', data: item });
      }
    };

    const handleQtyShort = (item, type = '') => {
      state.qtyShortOptionsModal = type;
      onTableCellClicked({ type: 'qtyShort', data: item });
    }

    const closeQtyShortOptionModal = () => {
      state.qtyShortOptionsModal = '';
      state.selectedShortItem = {};
      state.selectedQtyShortOption = '';
    }

    const getLoadData = () => {
      if (getApiMode()) return loadAsyncData;
      return loadData;
    };

    const getApiMode = () => {
      if (store.state.activeScreen === 'shipping-card-edit') { return true; }
      return !props.tab?.tableProps?.isCardView;
    };

    const onDeleteClicked = (payload) => {
      if (!payload) return;
      if (payload.type === 'delete') {
        confirmDelete(payload.data);
      }
    };

    const getQtyReceived = (row) => {
      return get(row, 'masterSummary.delivered', 0) - row.underDeliver;
    }

    const confirmDelete = (item) => {
      if (!item) return;
      let message = `<p class="has-text-weight-bold">Delete Item From Shipping Order?</p>`;
      if (item.source?.kind === 'ProductionOrder' && item.hasReservedMaterials) {
        message += `<p class="mb-4">Item will be removed from this Shipping Order and the item shipping status will be reset in the source Production Order.</p>
        <p>All reserved materials associated with this item will be returned to Inventory.</p>`;
      } else if (item.source?.kind === 'ProductionOrder') {
        message += `<p class="mb-4">Item will be removed from this Shipping Order and the item shipping status will be reset in the source Production Order.</p>
        <p>All reserved materials associated with this item will be returned to Inventory.</p>`;
      } else if (item.source?.from === 'inventory') {
        message += `Item will be removed from this Shipping Order and the full item quantity will be returned to Inventory.`;
      } else if (item.source?.from === 'card' || item.source?.kind === 'Part') {
        message += `Item will be removed from this Shipping Order and the item shipping status will be reset in the source Material Order.`;
      }

      DialogProgrammatic.confirm({
        title: 'Delete Item',
        message: `<p>Item <br/>${item.name}</p>
                  <div class="is-divider my-2"></div>
                  <p>${message}</p>`,
        okButton: 'Delete Item',
        cancelButton: 'Cancel',
        onConfirm: async () => {
          state.isLoading = true;
          try {
            await handleDeleteItem(item);
          } catch (error) {
            console.error('Deletion failed', error);
          } finally {
            state.isLoading = false;
          }
        },
      });
    };
    const handleDeleteItem = async (item) => {
      try {
        rowData.removeItem(item, 'fromSelf');
        const newShipData = await rowData.save();
        await props.refreshCard();
        state.refreshKey += 1;
        if( newShipData.itemElementsCount === 0 ) {
          await rowData.archive(rowData._id);
          router.replace({ path: '/logistics/shipping/order-view'});
        }
        toast.success(`${item.name} removed successfully`);
      } catch (err) {
        console.error(err);
      }
    };

    const disableDelete = (item) => {
      if ( get(item, state.unscheduledField)!== item.masterSummary?.total || (get(item, state.unscheduledField)?? 0) === 0) {
        return true;
      }
      return false;
    };

    const executeShortOption = () => {
      if (state.selectedQtyShortOption === 'ignoreShort') {
        DialogProgrammatic.confirm({
          title: `Ignore Short QTY ${shortTypeLabel.value}`,
          message: `<div class="is-size-3 has-text-black-bis">
                      <p class='is-italic has-text-weight-semibold'>Item</p>
                      <p class='is-italic'>${state.selectedShortItem.name}</p>
                      <div class="is-divider my-4 is-dark"></div>
                      <div class="is-flex is-justify-content-space-between">
                        <div>
                          <span class="is-italic has-text-weight-bold">Total Item QTY:</span> ${_.get(state.selectedShortItem, 'masterSummary.total', 0)}
                        </div>
                        <div>
                          <span class="is-italic has-text-weight-bold">QTY Short: </span>
                          ${_.get(state.selectedShortItem, state.qtyShortOptionsModal === 'receive' ? 'qtyShort' : 'qtyShortLoad', 0)}
                        </div>
                      </div>
                      <div class="has-text-weight-bold mt-4">Ignore Short QTY ${shortTypeLabel.value}?</div>
                      Ignoring “Short QTY” will remove the ability to reschedule this item and clear this shortage as a blocker.
                      The short QTY will remain noted in the associated delivery. <span class="has-text-weight-bold">THIS CANNOT BE UNDONE.</span>
                    </div>`,
          okButton: 'Ignore Short QTY',
          cancelButton: 'Back',
          cancelBtnIsOutlined: true,
          confirmButtonStyle: 'isSolid',
          onConfirm: async () => {
            state.isLoading = true;
            try {
              await Shipping.ignoreShort({
                itemId: state.selectedShortItem._id,
                parentId: props.rowData._id,
                type: state.qtyShortOptionsModal === 'receive' ? 'received' : 'load',
              });
              await props.refreshCard();
            } catch (error) {
              console.error('Ignore Short Failed', error);
            } finally {
              state.isLoading = false;
            }
          },
        });
      } else {
        ModalProgrammatic.open({
          component: ScheduleItems,
          props: {
            qtyShortItem: [state.selectedShortItem],
            card: state.selectedShortItem,
            masterShipment: {
              ...props.rowData,
              // items: filteredItems,
            },
            parentShipmentId: props.rowData._id,
            isActive: true,
            qtyShort: true,
            shortType: state.qtyShortOptionsModal === 'receive' ? 'received' : 'load',
          },
          rootClass: 'modal-sm',
          canCancel: false,
          events: {
            close: async () => {
              await props.refreshCard();
            },
          },
        });
      }
    }

    const getPopupContent = (type,itemName, currentQty, ignoredQty) => {
      return `
        <div class="is-size-2 has-text-weight-bold has-text-black-bis px-3 pt-3 pb-0"> QTY Short (${type})</div>
        <section class="card-content pt-0 is-marginless has-background-white has-text-black-bis">
          <div class="has-text-black-bis">
            <div class="has-text-weight-semibold is-italic pl-0 my-4">
              ${itemName}
            </div>
            <div class="is-flex is-justify-content-space-between mb-1">
              <div>Current QTY Short</div>
              <span class="has-text-weight-bold">
                ${currentQty}
              </span>
            </div>
            <div class="is-flex is-justify-content-space-between">
              <div>Ignored QTY Short</div>
              <span class="has-text-weight-bold">
                ${ignoredQty}
              </span>
            </div>
          </div>
        </section>`;
    }

    return {
      ...toRefs(state),
      shipItemsTable,
      onTableCellClicked,
      getLoadData,
      getApiMode,
      loadAsyncData,
      getQtyReceived,
      handleScheduleClick,
      confirmDelete,
      onDeleteClicked,
      disableDelete,
      handleQtyShort,
      closeQtyShortOptionModal,
      shortQtyOptions,
      executeShortOption,
      getPopupContent,
      shortTypeLabel
    }
  }
})
</script>

<style scoped>
  .modal-card-head {
    background-color: #fff !important;
    border-bottom: none !important;
  }
  .modal-card-foot {
    border-top: 1px solid #84888c;
     background-color: #fff !important;
  }
  .has-background-red {
    background-color: #D1000D !important;
  }
  ::v-deep(.v-popper) {
    width: auto !important;
  }
</style>
